import { useEffect, useState } from 'react';
import type { StyleSpecification } from 'maplibre-gl';
import { WATERWAYS } from '@whaletail/shared';

/**
 * Basemap styles.
 *
 * "Plain" starts from OpenFreeMap's stock Positron / Dark styles and recolors
 * them: a traditional warm-neutral land against clearly blue water, so the
 * shoreline reads at a glance (the stock styles render both as near-identical
 * grays). It also adds a label layer for the passages, straits and channels
 * that the OpenStreetMap-derived tiles leave unnamed.
 *
 * The recoloring is done by layer id on the fetched style JSON, so it keeps
 * working as OpenFreeMap tweaks other layers, and falls back to the untouched
 * stock style if anything about the fetch or patch goes wrong.
 */

export type Scheme = 'light' | 'dark';

const STYLE_URL: Record<Scheme, string> = {
  light: 'https://tiles.openfreemap.org/styles/positron',
  dark: 'https://tiles.openfreemap.org/styles/dark',
};

interface Palette {
  land: string;
  park: string;
  wood: string;
  residential: string;
  water: string;
  waterway: string;
  waterLabel: string;
  waterHalo: string;
}

const PALETTE: Record<Scheme, Palette> = {
  light: {
    land: '#ebe7dd',
    park: '#dbe4cf',
    wood: '#d4dec7',
    residential: '#e3ded2',
    water: '#a8cee8',
    waterway: '#8dbde0',
    waterLabel: '#2b6690',
    waterHalo: 'rgba(255, 255, 255, 0.85)',
  },
  dark: {
    land: '#2d2f30',
    park: '#323a31',
    wood: '#2e352e',
    residential: '#2a2c2c',
    water: '#1b3a56',
    waterway: '#25517c',
    waterLabel: '#8fc0e6',
    waterHalo: 'rgba(8, 19, 31, 0.85)',
  },
};

/** Layer ids (across both stock styles) whose label color is the water color. */
const WATER_LABEL_LAYERS = new Set(['water_name', 'water_name_point_label', 'water_name_line_label', 'waterway_line_label']);

/**
 * Road-name labels in OpenFreeMap's dark style (`highway_name_other` and
 * `highway_name_motorway`) are a dark gray tuned for that style's own
 * near-black background. Recoloring the background to our lighter charcoal
 * land color leaves them close to unreadable — dark gray text on a dark gray
 * fill — so they get the same treatment as everything else here: bumped to a
 * near-white that reads clearly against it. The light style's road labels
 * (`highway-name-minor`/`-major`, a mid gray) already read fine against the
 * warm-neutral land color and are left alone.
 */
const DARK_ROAD_LABEL_LAYERS = new Set(['highway_name_other', 'highway_name_motorway']);
const DARK_ROAD_LABEL_COLOR = '#e8e6e1';

type Layer = StyleSpecification['layers'][number];

/** Recolor the stock style. Pure, so it is easy to test. */
export function patchStyle(style: StyleSpecification, scheme: Scheme): StyleSpecification {
  const p = PALETTE[scheme];
  const layers = style.layers.map((layer): Layer => {
    const next = { ...layer, paint: { ...((layer as { paint?: object }).paint ?? {}) } } as Layer & {
      paint: Record<string, unknown>;
    };
    switch (layer.id) {
      case 'background':
        next.paint['background-color'] = p.land;
        break;
      case 'water':
        next.paint['fill-color'] = p.water;
        break;
      case 'waterway':
        next.paint['line-color'] = p.waterway;
        break;
      case 'park':
      case 'landuse_park':
        next.paint['fill-color'] = p.park;
        break;
      case 'landcover_wood':
        next.paint['fill-color'] = p.wood;
        break;
      case 'landuse_residential':
        next.paint['fill-color'] = p.residential;
        break;
      default:
        if (WATER_LABEL_LAYERS.has(layer.id)) {
          next.paint['text-color'] = p.waterLabel;
          next.paint['text-halo-color'] = p.waterHalo;
          next.paint['text-halo-width'] = 1.5;
        } else if (scheme === 'dark' && DARK_ROAD_LABEL_LAYERS.has(layer.id)) {
          next.paint['text-color'] = DARK_ROAD_LABEL_COLOR;
        }
    }
    return next;
  });

  return { ...style, layers: [...layers, ...waterwayLayers(p)] };
}

/**
 * Names for the waterways the base tiles do not label — Hale Passage, Colvos
 * Passage, Admiralty Inlet and so on — since those are what observers use to say
 * where whales are and where they are heading next. Three tiers keep the map
 * readable: the big bodies of water show when zoomed out, channels appear as you
 * come in, and narrow passages only once there is room to draw them.
 */
function waterwayLayers(p: Palette): Layer[] {
  const source = 'waterways';
  const common = {
    type: 'symbol' as const,
    source,
    layout: {
      'text-field': ['get', 'name'],
      'text-font': ['Noto Sans Italic'],
      'text-rotate': ['get', 'angle'],
      'text-rotation-alignment': 'map',
      'text-pitch-alignment': 'map',
      'text-padding': 4,
      'text-max-width': 8,
    },
    paint: {
      'text-color': p.waterLabel,
      'text-halo-color': p.waterHalo,
      'text-halo-width': 1.6,
    },
  };

  const tier = (id: string, n: number, minzoom: number, size: unknown, extra: Record<string, unknown> = {}) =>
    ({
      ...common,
      id,
      minzoom,
      filter: ['==', ['get', 'tier'], n],
      layout: { ...common.layout, 'text-size': size, ...extra },
    }) as unknown as Layer;

  return [
    tier('waterways-major', 1, 5.5, ['interpolate', ['linear'], ['zoom'], 6, 11, 9, 15, 12, 20], {
      'text-transform': 'uppercase',
      'text-letter-spacing': 0.18,
    }),
    tier('waterways-channel', 2, 8.5, ['interpolate', ['linear'], ['zoom'], 9, 10.5, 12, 15], {
      'text-letter-spacing': 0.06,
    }),
    tier('waterways-narrow', 3, 10, ['interpolate', ['linear'], ['zoom'], 10, 10, 13, 14]),
  ];
}

/** The GeoJSON behind the waterway labels. */
export function waterwaySource(): { type: 'geojson'; data: GeoJSON.FeatureCollection } {
  return {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: WATERWAYS.map((w) => ({
        type: 'Feature' as const,
        properties: { id: w.id, name: w.name, tier: w.tier, angle: w.angle },
        geometry: { type: 'Point' as const, coordinates: [w.lon, w.lat] },
      })),
    },
  };
}

const cache = new Map<Scheme, Promise<StyleSpecification>>();

/**
 * The recolored plain style, or the stock style URL if it cannot be fetched or
 * patched (offline, or OpenFreeMap changed shape). Either way the map renders.
 */
export async function loadPlainStyle(scheme: Scheme): Promise<StyleSpecification | string> {
  let pending = cache.get(scheme);
  if (!pending) {
    pending = fetch(STYLE_URL[scheme])
      .then((r) => {
        if (!r.ok) throw new Error(`style ${r.status}`);
        return r.json() as Promise<StyleSpecification>;
      })
      .then((json) => {
        const patched = patchStyle(json, scheme);
        patched.sources = { ...patched.sources, waterways: waterwaySource() };
        return patched;
      });
    cache.set(scheme, pending);
    // Do not cache a failure: the next attempt should try the network again.
    pending.catch(() => cache.delete(scheme));
  }
  try {
    return await pending;
  } catch {
    return STYLE_URL[scheme];
  }
}

export const currentScheme = (): Scheme =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

/**
 * The live color scheme, updating on an OS-level light/dark switch. Anything
 * that needs to pick a light- or dark-tuned color at render time (not just
 * read a CSS custom property, which the browser already recolors for free)
 * uses this — e.g. trackColor in api.ts, which computes its hsl() directly
 * rather than through a CSS variable, since track colors are per-track and
 * unbounded, not a small fixed enum theme.css can define variables for.
 */
export function useColorScheme(): Scheme {
  const [scheme, setScheme] = useState<Scheme>(currentScheme);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setScheme(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return scheme;
}
